/**
 * O Portal externo — a casca e a máquina de estados da sessão (lotes P2/P3).
 *
 * ## O arquivo tinha 85 linhas e fazia tudo (achado CODE-01)
 *
 * Login, escolha de empresa, as duas experiências, a tabela de comissões, os cards de
 * campanha, a posição fiscal, os documentos e o download moravam aqui, em linhas de
 * quatrocentos caracteres. Nada disso era testável em separado, e cada correção
 * arriscava as outras seis coisas.
 *
 * Agora este arquivo faz UMA coisa: decide em que estado a sessão está e monta a
 * experiência da visão do portal. O resto está em `funcionalidades/`, um arquivo por
 * coisa que a tela faz.
 *
 * ## O contexto é o PORTAL, e dentro dele a empresa (D-131)
 *
 * A pessoa entra num portal (nome, UMA visão, N empresas). Com um portal só ela entra direto;
 * com vários, escolhe. Dentro de um portal de várias empresas ela vê "Todas" ou escolhe uma no
 * seletor da página. Nenhuma das duas escolhas vai para a URL (R-05): as duas são um POST de
 * contexto, e a tela só troca quando a sessão CONFIRMA o portal e a empresa pedidos.
 *
 * ## 401 volta ao login, sem shell
 *
 * A sessão pode morrer com a tela aberta — revogar o acesso ao portal mata na hora (spec 02
 * §13.2). Antes disso virava um cartão vermelho dentro do portal, com o header e o nome
 * da empresa ainda na tela: a pessoa continuava vendo a moldura de um acesso que já não
 * existia. Agora o 401 sobe até aqui, limpa a identidade e devolve o cartão de login com
 * o banner "Sua sessão foi encerrada. Entre novamente."
 *
 * ## O recorte da tela é dito em toda leitura
 *
 * O contexto é da sessão, e a sessão é das abas todas: outra aba pode escolher outra empresa, e
 * o administrador pode tirar do portal a empresa escolhida. A tela diz em cada leitura o recorte
 * que assumiu; o servidor recusa o que diverge com 409, e este arquivo relê a sessão e remonta a
 * tela no recorte novo, dizendo qual é.
 */
import { StrictMode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import {
  euExternoDoPortal,
  type EmpresaDoPortalNoExterno,
  type EuExternoDoPortal,
  type PortalDoExterno,
} from '@dv/contracts'
import { dataNominal, type DataNominal } from '@dv/domain'
import { z } from 'zod'
import { aoMudarContexto, assumirContexto, chaveDoContexto, contextoAssumido, enviar, pedir } from './api/cliente.js'
import { FalhaDoPortal } from './api/erros.js'
import { aoPerderSessao } from './estado/recurso.js'
import { empresasDaTela, ProvedorDeEmpresas } from './estado/empresas.js'
import { ComoLerEstaPagina } from './componentes/como-ler.js'
import { MenuDoUsuario } from './componentes/menu-do-usuario.js'
import { SeletorDeEmpresa } from './componentes/seletor-de-empresa.js'
import { ProvedorDeAvisos, useAviso, useLimparAvisos } from './componentes/toast.js'
import { Login } from './funcionalidades/login.js'
import { EscolhaDoPortal } from './funcionalidades/chooser.js'
import { EsqueciSenha, limparModoDaUrl, RedefinirSenha, resetDaUrl } from './funcionalidades/recuperacao.js'
import { ExperienciaDoCliente } from './funcionalidades/cliente.js'
import { ExperienciaDoParceiro } from './funcionalidades/parceiro.js'
import { nomeDaVisao } from './formato.js'
import './fontes.css'
import './portal.css'
import './login.css'
import './download.css'

/** Qual controle recebe o foco depois da remontagem: o menu da conta ou o seletor de empresa. */
type Foco = 'conta' | 'empresa'

type Estado =
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'login'; readonly sessaoEncerrada: boolean; readonly emailDoConvite: string | null }
  | { readonly tipo: 'convite-invalido' }
  /* Os dois modos da recuperação (P1). Mesmo cartão, sem tela nova (spec 11.3). */
  | { readonly tipo: 'esqueci' }
  | { readonly tipo: 'redefinir'; readonly token: string }
  | { readonly tipo: 'escolha'; readonly eu: EuExternoDoPortal }
  /* `empresa` null = Todas as empresas do portal. */
  | {
      readonly tipo: 'portal'
      readonly eu: EuExternoDoPortal
      readonly portal: PortalDoExterno
      readonly empresa: EmpresaDoPortalNoExterno | null
      /** Cresce a cada montagem: remonta a tela mesmo quando o recorte relido é o mesmo. */
      readonly versao: number
      readonly focar?: Foco
    }

/** O que se estava fazendo quando o contexto foi recusado — é o que o aviso de falha nomeia. */
type Motivo = 'entrar' | 'portal' | 'empresa'

const FALHA_DO_CONTEXTO: Readonly<Record<Motivo, { readonly titulo: string; readonly texto: string }>> = {
  entrar: { titulo: 'Não foi possível entrar no portal', texto: 'Seu acesso a ele pode ter mudado. Tente de novo em instantes.' },
  portal: { titulo: 'Não foi possível trocar de portal', texto: 'Seu acesso a ele pode ter mudado. Tente de novo em instantes.' },
  empresa: { titulo: 'Não foi possível trocar de empresa', texto: 'Seu acesso a ela pode ter mudado. Tente de novo em instantes.' },
}

const donoDoConvite = z.object({ email: z.string(), nome: z.string() })
const respostaDeLogout = z.object({ ok: z.literal(true) })

/** O dia de hoje, uma vez, na montagem — nenhum componente lê o relógio por conta. */
function hojeDoNavegador(): DataNominal {
  const agora = new Date()
  const dois = (n: number): string => String(n).padStart(2, '0')
  return dataNominal(`${String(agora.getFullYear())}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}`)
}

function App() {
  const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' })
  const [hoje] = useState<DataNominal>(hojeDoNavegador)
  const avisar = useAviso()
  const limparAvisos = useLimparAvisos()
  const montagens = useRef(0)
  const relendoSessao = useRef(false)

  /**
   * Monta a tela num recorte CONFIRMADO pela sessão — e é daqui que cada leitura passa a dizê-lo.
   *
   * O recorte é assumido ANTES de o estado mudar: os blocos da tela remontada pedem no efeito da
   * montagem, e o primeiro pedido deles já tem de levar o recorte novo. Um pedido ainda no ar da
   * tela anterior levou o recorte dela, e a resposta dele é descartada com a tela.
   */
  const mostrarPortal = useCallback(
    (eu: EuExternoDoPortal, portal: PortalDoExterno, empresa: EmpresaDoPortalNoExterno | null, focar?: Foco) => {
      assumirContexto(chaveDoContexto(portal.id, empresa?.id ?? null))
      montagens.current += 1
      setEstado({ tipo: 'portal', eu, portal, empresa, versao: montagens.current, ...(focar === undefined ? {} : { focar }) })
    },
    [],
  )

  const avancar = useCallback((eu: EuExternoDoPortal) => {
    const atual = eu.portais.find((p) => p.id === eu.portalAtualId)
    if (atual !== undefined) {
      const empresa = eu.empresaAtualId === null ? null : atual.empresas.find((e) => e.id === eu.empresaAtualId)
      if (empresa !== undefined) {
        mostrarPortal(eu, atual, empresa)
        return
      }
      // A sessão aponta uma empresa que o portal não lista: a tela não nomeia o que não
      // conhece. Pede o contexto de novo, em Todas, e só entra com a confirmação.
      void escolherContexto(eu, atual, null, 'entrar')
      return
    }
    // Um portal só: não há escolha a fazer, e mostrar uma lista de um item seria pedir uma
    // decisão que não existe. Ele abre em Todas as empresas dele.
    const unico = eu.portais.length === 1 ? eu.portais[0] : undefined
    if (unico !== undefined) {
      void escolherContexto(eu, unico, null, 'entrar')
      return
    }
    setEstado({ tipo: 'escolha', eu })
  }, [mostrarPortal])

  /**
   * O POST de contexto — e a tela só troca com a CONFIRMAÇÃO da sessão (AB-026).
   *
   * A resposta tem de dizer o mesmo portal E a mesma empresa pedidos (ou Todas, quando se
   * pediu Todas). Qualquer divergência vale como recusa: nenhum número pode aparecer sob um
   * portal ou uma empresa que a sessão não assumiu.
   */
  async function escolherContexto(
    eu: EuExternoDoPortal,
    portal: PortalDoExterno,
    empresa: EmpresaDoPortalNoExterno | null,
    motivo: Motivo,
    focar?: Foco,
  ): Promise<boolean> {
    try {
      const atualizado = await enviar('contexto', { portal_id: portal.id, empresa_id: empresa?.id ?? null }, euExternoDoPortal)
      const confirmado = atualizado.portais.find((p) => p.id === atualizado.portalAtualId)
      const empresaConfirmada = atualizado.empresaAtualId === null
        ? null
        : confirmado?.empresas.find((e) => e.id === atualizado.empresaAtualId)
      if (confirmado?.id !== portal.id || empresaConfirmada === undefined || empresaConfirmada?.id !== empresa?.id) {
        // Sem confirmação da sessão, nenhum dado pode ficar sob o portal ou a empresa pedidos.
        assumirContexto(null)
        setEstado({ tipo: 'escolha', eu: atualizado })
        throw new FalhaDoPortal('contrato', 'A sessão não confirmou o portal e a empresa escolhidos.')
      }
      mostrarPortal(atualizado, confirmado, empresaConfirmada, focar)
      return true
    } catch (falha) {
      if (falha instanceof FalhaDoPortal && falha.tipo === 'sessao') {
        encerrarSessao()
        return false
      }
      // Inclui a entrada automática no portal único e a primeira escolha manual.
      setEstado(anterior => anterior.tipo === 'carregando' || anterior.tipo === 'login'
        ? { tipo: 'escolha', eu } : anterior)
      avisar({ tom: 'erro', ...FALHA_DO_CONTEXTO[motivo] })
      return false
    }
  }

  /** A sessão morreu no meio do uso: sai do portal INTEIRO, não mostra cartão de erro. */
  const encerrarSessao = useCallback(() => {
    limparAvisos()
    // Idem no fim de sessão: sem isto, quem entrou por um convite e ficou horas com a
    // aba aberta reencontraria o cartão de aceite quando a sessão caísse.
    limparModoDaUrl()
    assumirContexto(null)
    setEstado({ tipo: 'login', sessaoEncerrada: true, emailDoConvite: null })
  }, [limparAvisos])

  /**
   * Uma leitura voltou com o 409 do contexto que mudou (D-131): relê a sessão e remonta a tela
   * no recorte que ela tem agora, dizendo qual é.
   *
   * `assumido` é o recorte que a leitura recusada levou. Se a tela já assumiu outro, a recusa é de
   * uma tela que está saindo de cena, e não pede nada. Os blocos de uma tela recebem o 409 quase
   * juntos: a sessão é relida uma vez só. A remontagem acontece mesmo quando o recorte relido é o
   * mesmo — os blocos que levaram o 409 voltam a pedir —, e aí sem aviso, porque nada mudou.
   */
  const contextoMudou = useCallback(
    async (assumido: string): Promise<void> => {
      if (assumido !== contextoAssumido() || relendoSessao.current) return
      relendoSessao.current = true
      try {
        const eu = await pedir('me', euExternoDoPortal)
        const portal = eu.portais.find((p) => p.id === eu.portalAtualId)
        const empresa = eu.empresaAtualId === null ? null : portal?.empresas.find((e) => e.id === eu.empresaAtualId)
        if (portal === undefined || empresa === undefined) {
          // Um recorte que a tela não sabe nomear: nenhum número fica na tela, e a entrada decide.
          assumirContexto(null)
          setEstado({ tipo: 'carregando' })
          avancar(eu)
          return
        }
        const novo = chaveDoContexto(portal.id, empresa?.id ?? null)
        const outroPortal = !assumido.startsWith(`${portal.id}:`)
        mostrarPortal(eu, portal, empresa, novo === assumido ? undefined : outroPortal ? 'conta' : 'empresa')
        if (novo === assumido) return
        avisar(
          outroPortal
            ? { tom: 'atencao', titulo: 'Portal alterado', texto: `Agora exibindo ${portal.nome}.` }
            : {
                tom: 'atencao',
                titulo: 'Empresa alterada',
                texto: empresa === null ? 'Agora exibindo todas as empresas.' : `Agora exibindo ${empresa.nome}.`,
              },
        )
      } catch (falha) {
        if (falha instanceof FalhaDoPortal && falha.tipo === 'sessao') {
          encerrarSessao()
          return
        }
        // Sem a sessão relida, nenhum número pode ficar sob o recorte antigo: volta à escolha.
        assumirContexto(null)
        setEstado((anterior) => (anterior.tipo === 'portal' ? { tipo: 'escolha', eu: anterior.eu } : anterior))
        avisar({ tom: 'erro', titulo: 'Não foi possível atualizar o portal', texto: 'Seu acesso a ele pode ter mudado. Escolha o portal de novo.' })
      } finally {
        relendoSessao.current = false
      }
    },
    [avancar, avisar, encerrarSessao, mostrarPortal],
  )

  // O cliente HTTP avisa aqui cada 409 de contexto — dos blocos e do clique de download.
  useEffect(() => {
    aoMudarContexto((assumido) => {
      void contextoMudou(assumido)
    })
    return () => {
      aoMudarContexto(null)
    }
  }, [contextoMudou])

  /**
   * As duas trocas acontecem NA PÁGINA, e por isso as duas avisam.
   *
   * Antes, trocar era ir até a tela de escolha e voltar: a página inteira saía de cena
   * para mudar um contexto que a sessão troca em um pedido. Agora o menu do cabeçalho troca
   * o PORTAL e o seletor da página troca a EMPRESA, no lugar — e como quase nada na moldura
   * muda enquanto TODOS os números mudam, o aviso nomeia o que passou a valer. Sem ele, quem
   * clicasse na linha errada leria os números de outro recorte achando que são os do primeiro.
   */
  async function trocarPortal(portal: PortalDoExterno): Promise<void> {
    if (estado.tipo !== 'portal') return
    // O portal novo abre em Todas, como na entrada: a empresa escolhida era do anterior.
    const trocou = await escolherContexto(estado.eu, portal, null, 'portal', 'conta')
    if (trocou) avisar({ tom: 'sucesso', titulo: 'Portal alterado', texto: `Agora exibindo ${portal.nome}.` })
  }

  async function trocarEmpresa(empresa: EmpresaDoPortalNoExterno | null): Promise<void> {
    if (estado.tipo !== 'portal') return
    const trocou = await escolherContexto(estado.eu, estado.portal, empresa, 'empresa', 'empresa')
    if (trocou) {
      avisar({ tom: 'sucesso', titulo: 'Empresa alterada',
        texto: empresa === null ? 'Agora exibindo todas as empresas.' : `Agora exibindo ${empresa.nome}.` })
    }
  }

  useEffect(() => {
    let vivo = true
    const convite = new URLSearchParams(location.search).get('convite')
    const reset = resetDaUrl()

    async function abrir(): Promise<void> {
      /*
        O link de redefinição vence a sessão que existir.

        Sem esta saída, quem clicasse no link com uma sessão viva no navegador cairia
        direto no portal — e a pessoa que pediu a recuperação PORQUE perdeu o acesso
        (ou porque desconfia de que outra pessoa o tem) nunca chegaria ao formulário.
        Trocar a senha é justamente o que derruba a sessão de quem não deveria estar
        lá; ir para o portal primeiro deixaria essa sessão de pé.
      */
      if (reset !== null) {
        if (vivo) setEstado({ tipo: 'redefinir', token: reset })
        return
      }
      if (convite !== null) {
        /*
          Convite: resolver o dono ANTES de mostrar o formulário.

          Um convite vencido abria o cartão como se estivesse tudo bem, a pessoa criava
          uma senha e só então recebia a recusa. A spec 11.3 pede um estado nomeado — e
          é o que faz alguém pedir um convite novo em vez de tentar de novo.
        */
        try {
          const dono = await pedir(`auth/convite?token=${encodeURIComponent(convite)}`, donoDoConvite)
          if (vivo) setEstado({ tipo: 'login', sessaoEncerrada: false, emailDoConvite: dono.email })
        } catch {
          if (vivo) setEstado({ tipo: 'convite-invalido' })
        }
        return
      }

      try {
        const eu = await pedir('me', euExternoDoPortal)
        if (vivo) avancar(eu)
      } catch {
        if (vivo) setEstado({ tipo: 'login', sessaoEncerrada: false, emailDoConvite: null })
      }
    }

    void abrir()
    return () => {
      vivo = false
    }
  }, [avancar])

  async function sair(): Promise<void> {
    try {
      await enviar('auth/logout', {}, respostaDeLogout)
    } catch {
      // Sair é um ato do usuário: se o servidor não respondeu, a tela ainda tem de sair.
    }
    limparAvisos()
    limparModoDaUrl()
    assumirContexto(null)
    setEstado({ tipo: 'login', sessaoEncerrada: false, emailDoConvite: null })
  }

  if (estado.tipo === 'carregando') return <div className="loading">Carregando…</div>

  if (estado.tipo === 'convite-invalido') {
    return (
      <main className="entrada">
        {/* A logo em imagem sobre o fundo claro, como no login e na recuperação (PK-06A). */}
        <img className="marca-login" src="/marca/logo-digitalview-navy.png" alt="DigitalView" width={168} height={33} />
        <section className="login-card">
          <p className="eyebrow">ACESSO EXTERNO</p>
          <h1>Convite inválido ou vencido</h1>
          <p className="intro">Peça um novo convite ao seu contato na DigitalView.</p>
        </section>
      </main>
    )
  }

  if (estado.tipo === 'esqueci') {
    return (
      <EsqueciSenha
        aoVoltar={() => {
          // A limpeza acontece AQUI, e não no componente: quem volta ao login é este
          // arquivo, e é o `?convite=` remanescente que faria o login desenhar o
          // cartão de aceite em vez do de entrada.
          limparModoDaUrl()
          setEstado({ tipo: 'login', sessaoEncerrada: false, emailDoConvite: null })
        }}
      />
    )
  }

  if (estado.tipo === 'redefinir') {
    return (
      <RedefinirSenha
        token={estado.token}
        aoEntrar={avancar}
        aoPedirNovoLink={() => {
          setEstado({ tipo: 'esqueci' })
        }}
      />
    )
  }

  if (estado.tipo === 'login') {
    return (
      <>
        {estado.sessaoEncerrada && (
          <div className="banner-sessao" role="status" data-teste="banner-sessao">
            Sua sessão foi encerrada. Entre novamente.
          </div>
        )}
        <Login
          aoEntrar={avancar}
          emailDoConvite={estado.emailDoConvite}
          aoEsquecerSenha={() => {
            setEstado({ tipo: 'esqueci' })
          }}
        />
      </>
    )
  }

  if (estado.tipo === 'escolha') {
    return (
      <EscolhaDoPortal
        eu={estado.eu}
        escolher={(portal) => {
          void escolherContexto(estado.eu, portal, null, 'entrar')
        }}
        sair={() => {
          void sair()
        }}
      />
    )
  }

  return (
    <Portal
      /*
        A chave é o que faz a troca in-place TROCAR de fato.

        Nenhuma consulta dos blocos leva o portal ou a empresa na chave — o contexto é da
        SESSÃO, e pôr a empresa na URL de cada pedido seria justamente o que a R-05 proíbe.
        Sem remontar a árvore, React reaproveitaria os efeitos e a tela ficaria com os
        números do recorte anterior sob o nome do novo. Por isso a chave leva os DOIS: trocar
        só a empresa, dentro do mesmo portal, também tem de remontar. E a versão: relida a
        sessão depois de um 409 de contexto, a tela remonta mesmo que o recorte seja o mesmo.
      */
      key={`${chaveDoContexto(estado.portal.id, estado.empresa?.id ?? null)}#${String(estado.versao)}`}
      eu={estado.eu}
      portal={estado.portal}
      empresa={estado.empresa}
      focar={estado.focar ?? null}
      hoje={hoje}
      trocarPortal={(portal) => {
        void trocarPortal(portal)
      }}
      trocarEmpresa={(empresa) => {
        void trocarEmpresa(empresa)
      }}
      sair={() => {
        void sair()
      }}
      perdeuSessao={encerrarSessao}
    />
  )
}

function Portal({
  eu,
  portal,
  empresa,
  hoje,
  trocarPortal,
  trocarEmpresa,
  sair,
  perdeuSessao,
  focar,
}: {
  eu: EuExternoDoPortal
  portal: PortalDoExterno
  /** null = Todas as empresas do portal. */
  empresa: EmpresaDoPortalNoExterno | null
  hoje: DataNominal
  trocarPortal: (portal: PortalDoExterno) => void
  trocarEmpresa: (empresa: EmpresaDoPortalNoExterno | null) => void
  sair: () => void
  perdeuSessao: () => void
  focar: Foco | null
}) {
  const parceiro = portal.visao === 'Parceiro'
  const variasEmpresas = portal.empresas.length > 1
  const [rotuloDoPeriodo, setRotuloDoPeriodo] = useState<string | null>(null)
  const empresas = useMemo(() => empresasDaTela(portal, empresa), [portal, empresa])
  /*
    O nome do recorte, no subtítulo: a empresa escolhida; a única empresa de um portal de uma
    só; ou o próprio portal, em Todas. Num portal de uma empresa, o nome dela diz mais do que o
    nome que a DigitalView deu ao portal.
  */
  const recorte = empresa?.nome ?? (variasEmpresas ? portal.nome : (portal.empresas[0]?.nome ?? portal.nome))

  /*
    A queda de sessão é global, e por isso o ouvinte é global.

    Cada bloco da tela faz o seu próprio pedido; qualquer um deles pode receber 401. A
    máquina de recursos avisa aqui, uma vez, e a casca inteira sai do portal — em vez de
    sete cartões de erro dentro de uma moldura que já não vale.
  */
  useEffect(() => {
    aoPerderSessao(perdeuSessao)
    return () => {
      aoPerderSessao(null)
    }
  }, [perdeuSessao])

  return (
    <>
      {/*
        O cabeçalho do protótipo 11.x (PK-09): branco, 64px, a logo em imagem e a visão como
        eyebrow com filete à esquerda. O navy com o nome escrito à mão era um quinto desenho
        da marca, e nenhum protótipo o previa.
      */}
      <header>
        <img className="logo-portal" src="/marca/logo-digitalview-navy.png" alt="DigitalView" width={112} height={22} />
        <span className="eyebrow visao-do-portal" data-teste="visao-do-portal">{nomeDaVisao(portal.visao)}</span>
        <span className="espaco" />
        {/* O portal vive no menu, junto de quem está logado; a empresa, na página. */}
        <MenuDoUsuario focarAoMontar={focar === 'conta'} eu={eu} portal={portal} trocarPara={trocarPortal} sair={sair} />
      </header>
      <main className="conteudo" id="conteudo">
        {/*
          O período fica NA LINHA do "Bem-vindo" (D-77, spec 11.1:60 e 11.2:31): ele diz o recorte
          da página inteira. Quem o conhece é a experiência, que o devolve para cá.
        */}
        <div className="boas-vindas">
          <p className="eyebrow">BEM-VINDO, {eu.nome.toUpperCase()}</p>
          {rotuloDoPeriodo !== null && (
            <span className="rotulo-periodo" data-teste="rotulo-periodo">
              · {rotuloDoPeriodo}
            </span>
          )}
        </div>
        <div className="titulo-da-pagina">
          <h1>{parceiro ? 'Suas comissões e negócios' : 'Suas campanhas e documentos'}</h1>
          <ComoLerEstaPagina parceiro={parceiro} variasEmpresas={variasEmpresas} />
        </div>
        {/*
          A empresa governa a página INTEIRA, como o período governa a linha do "Bem-vindo":
          por isso o seletor mora numa linha própria junto do título, e não nos filtros de uma
          seção. Só existe com mais de uma empresa: com uma, não há o que escolher.
        */}
        {variasEmpresas && (
          <SeletorDeEmpresa portal={portal} empresa={empresa} focarAoMontar={focar === 'empresa'} aoTrocar={trocarEmpresa} />
        )}
        {/*
          O subtítulo do parceiro é escrito pela PRÓPRIA experiência: ele diz o
          percentual acordado, e esse número vive nas comissões, que só o bloco de baixo
          conhece. Escrito aqui em cima, ele sairia genérico ou inventado.
        */}
        {/*
          O do cliente diz só o recorte (D-203): "campanhas, notas e documentos da sua empresa" repetia o título.
        */}
        {!parceiro && (
          <p className="subtitulo-da-pagina" data-teste="subtitulo">
            {recorte}
          </p>
        )}
        <ProvedorDeEmpresas value={empresas}>
          {parceiro ? (
            <ExperienciaDoParceiro hoje={hoje} empresa={recorte} aoMudarPeriodo={setRotuloDoPeriodo} />
          ) : (
            <ExperienciaDoCliente hoje={hoje} aoMudarPeriodo={setRotuloDoPeriodo} />
          )}
        </ProvedorDeEmpresas>
        {/*
          O rodapé mora DENTRO do conteúdo, como o último filho (D-134 · DS-531): fora dele, o filete
          passava 32px de cada lado da coluna e o respiro até a última seção somava a margem ao
          padding do conteúdo.
        */}
        <footer>
          {/* As duas pontas do protótipo (11.2:183). O plural da 11.1 vale também para o
              cliente cujo portal reúne mais de uma empresa. */}
          <span>© 2026 DigitalView</span>
          <span>
            Acesso somente leitura · você vê apenas os dados{' '}
            {parceiro || variasEmpresas ? 'das empresas que representa' : 'da sua empresa'}
          </span>
        </footer>
      </main>
    </>
  )
}

const raiz = document.getElementById('raiz')
if (raiz === null) throw new Error('Elemento raiz ausente.')
createRoot(raiz).render(
  <StrictMode>
    {/* A bandeja fica ACIMA do App: o aviso da troca de portal ou de empresa sobrevive à troca. */}
    <ProvedorDeAvisos>
      <App />
    </ProvedorDeAvisos>
  </StrictMode>,
)
